import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ApiIcon from '@mui/icons-material/Api';
import AppsIcon from '@mui/icons-material/Apps';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import DoorSlidingIcon from '@mui/icons-material/DoorSliding';
import GridViewIcon from '@mui/icons-material/GridView';
import HelpIcon from '@mui/icons-material/Help';
import HomeIcon from '@mui/icons-material/Home';
import HubIcon from '@mui/icons-material/Hub';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import KeyIcon from '@mui/icons-material/Key';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RadarIcon from '@mui/icons-material/Radar';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TerminalIcon from '@mui/icons-material/Terminal';
import { IconButton } from '@mui/material';
import React, {
  type ElementType,
  type PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import { NavLink } from 'react-router-dom';
import LogoIcon from './LogoIcon';
import './PlatformConsole.css';

interface NavigationItem {
  label: string;
  to: string;
  icon: ElementType;
}

const primaryNavigation: NavigationItem[] = [
  { label: 'Home', to: '/', icon: HomeIcon },
  { label: 'Catalog', to: '/catalog', icon: AppsIcon },
  { label: 'APIs', to: '/api-docs', icon: ApiIcon },
  { label: 'Ask Nora', to: '/nora', icon: SmartToyIcon },
  { label: 'Argo CD', to: '/argocd', icon: HubIcon },
  { label: 'OpenBao', to: '/openbao', icon: KeyIcon },
  { label: 'Kubernetes', to: '/kubernetes', icon: CloudQueueIcon },
  { label: 'Docs', to: '/docs', icon: LibraryBooksIcon },
  { label: 'Access Control', to: '/access-control', icon: DoorSlidingIcon },
  { label: 'Self-Service', to: '/create', icon: AddCircleIcon },
  { label: 'Tasks', to: '/tasks', icon: AssignmentIcon },
];

const secondaryNavigation: NavigationItem[] = [
  { label: 'Tech Radar', to: '/tech-radar', icon: RadarIcon },
  { label: 'Lighthouse', to: '/lighthouse', icon: AssessmentIcon },
];

function NavigationItems({
  items,
  onNavigate,
}: {
  items: NavigationItem[];
  onNavigate: () => void;
}) {
  return (
    <>
      {items.map(item => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            title={item.label}
            onClick={onNavigate}
            className={({ isActive }) =>
              `oc-nav-item ${isActive ? 'oc-nav-item--active' : ''}`
            }
          >
            <Icon className="oc-nav-item__icon" />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </>
  );
}

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const identityApi = useApi(identityApiRef);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [navigationCompact, setNavigationCompact] = useState(
    () => window.localStorage.getItem('platform-console-nav-compact') === 'true',
  );
  const [displayName, setDisplayName] = useState('PostNord user');

  useEffect(() => {
    identityApi
      .getProfileInfo()
      .then(profile =>
        setDisplayName(profile.displayName ?? profile.email ?? 'PostNord user'),
      )
      .catch(() => setDisplayName('PostNord user'));
  }, [identityApi]);

  useEffect(() => {
    window.localStorage.setItem(
      'platform-console-nav-compact',
      String(navigationCompact),
    );
  }, [navigationCompact]);

  const toggleNavigation = () => {
    if (window.matchMedia('(max-width: 760px)').matches) {
      setNavigationOpen(value => !value);
      return;
    }
    setNavigationCompact(value => !value);
  };

  return (
    <div
      className={`oc-shell ${
        navigationCompact ? 'oc-shell--compact' : ''
      }`}
    >
      <header className="oc-masthead">
        <div className="oc-masthead__brand">
          <IconButton
            className="oc-masthead__menu"
            onClick={toggleNavigation}
            aria-label="Toggle navigation"
            title="Toggle navigation"
          >
            {navigationOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
          <NavLink to="/" className="oc-brand">
            <LogoIcon className="oc-brand__logo" />
            <span className="oc-brand__name">
              <strong>PostNord</strong>
              <span>Developer Hub</span>
            </span>
          </NavLink>
        </div>

        <div className="oc-masthead__tools">
          <NavLink
            to="/search"
            className="oc-masthead__search"
            aria-label="Search"
          >
            <SearchIcon />
            <span>Search</span>
            <kbd>/</kbd>
          </NavLink>
          <NavLink
            to="/catalog"
            className="oc-masthead__icon-link"
            aria-label="Application launcher"
            title="Applications"
          >
            <GridViewIcon />
          </NavLink>
          <NavLink
            to="/tasks"
            className="oc-masthead__icon-link"
            aria-label="Notifications and tasks"
            title="Tasks"
          >
            <NotificationsIcon />
          </NavLink>
          <NavLink
            to="/create"
            className="oc-masthead__icon-link"
            aria-label="Create"
            title="Create"
          >
            <AddCircleIcon />
          </NavLink>
          <NavLink
            to="/sourcebot"
            className="oc-masthead__icon-link"
            aria-label="Source workspace"
            title="Source workspace"
          >
            <TerminalIcon />
          </NavLink>
          <NavLink
            to="/docs"
            className="oc-masthead__icon-link"
            aria-label="Help"
            title="Documentation"
          >
            <HelpIcon />
          </NavLink>
          <NavLink to="/settings" className="oc-user-menu">
            <span className="oc-user-menu__avatar">
              {displayName.charAt(0).toUpperCase()}
            </span>
            <span className="oc-user-menu__name">{displayName}</span>
            <ChevronRightIcon />
          </NavLink>
        </div>
      </header>

      <div
        className={`oc-shell__backdrop ${
          navigationOpen ? 'oc-shell__backdrop--open' : ''
        }`}
        onClick={() => setNavigationOpen(false)}
        aria-hidden
      />
      <aside
        className={`oc-sidebar ${navigationOpen ? 'oc-sidebar--open' : ''}`}
      >
        <nav className="oc-sidebar__nav" aria-label="Primary navigation">
          <NavigationItems
            items={primaryNavigation}
            onNavigate={() => setNavigationOpen(false)}
          />
          <div className="oc-nav-divider" />
          <NavigationItems
            items={secondaryNavigation}
            onNavigate={() => setNavigationOpen(false)}
          />
        </nav>
        <div className="oc-sidebar__footer">
          <NavLink
            to="/settings"
            title="Settings"
            className={({ isActive }) =>
              `oc-nav-item ${isActive ? 'oc-nav-item--active' : ''}`
            }
            onClick={() => setNavigationOpen(false)}
          >
            <SettingsIcon className="oc-nav-item__icon" />
            <span>Settings</span>
          </NavLink>
          <div className="oc-sidebar__version">
            <AutoAwesomeIcon />
            <span>Platform Console</span>
          </div>
        </div>
      </aside>

      <main className="oc-main" id="content-scrollable">
        {children}
      </main>
    </div>
  );
};
